"use client";
import { useState } from "react";
import { motion } from "motion/react";
import Centerpiece from "../General Components/Centerpiece";
import ResumeCard, { ResumeRow } from "../Contact/ResumeCard";

const ContactPageComp = ({ resumes }: { resumes: ResumeRow[] }) => {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [errorMsg, setErrorMsg] = useState("");

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus("loading");
    setErrorMsg("");

    const res = await fetch("/api/contact", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(form),
    });

    if (res.ok) {
      setStatus("success");
    } else {
      const data = await res.json().catch(() => ({}));
      setErrorMsg(data.message ?? "Something went wrong. Please try again.");
      setStatus("error");
    }
  };

  const HeroContent = () => (
    <div className="flex flex-col items-center justify-center h-full text-center px-4 py-12">
      <motion.h1
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="text-7xl font-bold"
        style={{
          textShadow:
            "var(--text-glow-color) 0px 0px 10px, var(--background) 0px 0px 8px",
        }}
      >
        Contact
      </motion.h1>

      <motion.div
        initial={{ scaleX: 0 }}
        animate={{ scaleX: 1 }}
        transition={{ duration: 0.7, delay: 0.4 }}
        className="bg-(--uforeground2) h-0.75 w-full max-w-120 my-5"
        style={{ transformOrigin: "center" }}
      />

      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6, delay: 0.6 }}
        className="text-xl text-(--foreground2) max-w-140"
      >
        Let&apos;s get in touch
      </motion.p>
    </div>
  );

  return (
    <div>
      <Centerpiece>{HeroContent()}</Centerpiece>

      <div className="mx-[4%] mb-16 flex flex-col lg:flex-row gap-10 lg:items-start">
        {/* Resume Downloads */}
        <div className="flex-1">
          <motion.h2
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-5xl mb-2"
          >
            Resumes
          </motion.h2>
          <motion.div
            initial={{ scaleX: 0 }}
            whileInView={{ scaleX: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="bg-(--uforeground2) h-0.75 mb-6"
            style={{ transformOrigin: "left" }}
          />
          <div className="grid grid-cols-1 gap-4">
            {resumes.length === 0 ? (
              <p className="text-sm text-(--foreground2)">No resumes available yet.</p>
            ) : (
              resumes.map((resume) => (
                <ResumeCard key={resume.id} resume={resume} />
              ))
            )}
          </div>
        </div>

        {/* Contact Form */}
        <div className="flex-1">
          <motion.h2
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-5xl mb-2"
          >
            Get in Touch
          </motion.h2>
          <motion.div
            initial={{ scaleX: 0 }}
            whileInView={{ scaleX: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="bg-(--uforeground2) h-0.75 mb-6"
            style={{ transformOrigin: "left" }}
          />

          {status === "success" ? (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              className="bg-(--background2) rounded-2xl p-10 border border-(--uforeground2) flex flex-col items-center gap-3 text-center"
            >
              <p className="text-2xl font-semibold">Thanks!</p>
              <p className="text-(--foreground2)">I&apos;ll be in touch soon.</p>
            </motion.div>
          ) : (
            <motion.form
              onSubmit={handleSubmit}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="bg-(--background2) rounded-2xl p-6 border border-(--uforeground2) flex flex-col gap-4"
            >
              <div className="flex flex-col gap-1">
                <label className="text-sm text-(--uforeground) font-medium">Name</label>
                <input
                  type="text"
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  placeholder="Your name"
                  required
                  className="rounded-xl p-3 text-sm border border-(--uforeground2) bg-(--background) text-(--foreground) outline-none focus:ring-2 focus:ring-(--uforeground2)"
                />
              </div>

              <div className="flex flex-col gap-1">
                <label className="text-sm text-(--uforeground) font-medium">Email</label>
                <input
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  placeholder="your@email.com"
                  required
                  className="rounded-xl p-3 text-sm border border-(--uforeground2) bg-(--background) text-(--foreground) outline-none focus:ring-2 focus:ring-(--uforeground2)"
                />
              </div>

              <div className="flex flex-col gap-1">
                <label className="text-sm text-(--uforeground) font-medium">Message</label>
                <textarea
                  name="message"
                  value={form.message}
                  onChange={handleChange}
                  placeholder="What's on your mind?"
                  required
                  rows={5}
                  className="rounded-xl p-3 text-sm border border-(--uforeground2) bg-(--background) text-(--foreground) outline-none focus:ring-2 focus:ring-(--uforeground2) resize-none"
                />
              </div>

              {status === "error" && (
                <p className="text-sm text-(--uforeground2)">{errorMsg}</p>
              )}

              <motion.button
                type="submit"
                disabled={status === "loading"}
                className="text-sm px-4 py-2 rounded-xl cursor-pointer font-medium disabled:opacity-50"
                style={{ backgroundColor: "var(--uforeground2)", color: "#ffffff" }}
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
              >
                {status === "loading" ? "Sending..." : "Send Message"}
              </motion.button>
            </motion.form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ContactPageComp;
